import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import { HFButton } from "@/components/ui-hf";
import { mediaThumb } from "@/lib/mediaThumb";
import { Plus, ArrowLeft, Trash2, ExternalLink, Copy, Eye, EyeOff, Video, Star, X, ImageIcon, Search } from "lucide-react";
import PairsEditor, { type Pair } from "@/components/PairsEditor";
import AiFlagToggle from "@/components/admin/AiFlagToggle";
import { useMediaAiFlags, setAiFlagLocal } from "@/lib/aiFlags";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

type Project = {
    id: string; slug: string; title: string; subtitle: string;
    description_short: string; description_long: string; object_type: string;
    location: string; capture_date: string | null; cover_media_id: string | null;
    visibility: "public" | "private"; password_protected: boolean;
    status: "draft" | "published"; order: number; media_ids: string[];
    show_on_main?: boolean; show_on_sylt?: boolean;
    pairs?: Pair[];
    is_demo?: boolean;
    created_at: string; updated_at: string;
};
type Media = { id: string; title: string; media_type: string; mime: string; poster_filename?: string | null; poster_url?: string | null; stored_url?: string | null; original_filename?: string; alt?: string; is_demo?: boolean; pair_id?: string | null; pair_role?: "before" | "after" | null };

const OBJECT_TYPES = ["Einfamilienhaus", "Villa", "Landhaus", "Wohnung", "Gewerblich", "Sonstiges"];

export function AdminProjekteListPage() {
    const [items, setItems] = useState<Project[]>([]);
    const [error, setError] = useState("");
    const nav = useNavigate();

    const load = useCallback(async () => {
        const res = await api<{ items: Project[] }>("/api/admin/projects");
        if (res.ok && res.data) setItems(res.data.items);
        else setError(res.error || "Konnte Projekte nicht laden.");
    }, []);
    useEffect(() => { void load(); }, [load]);

    const create = async () => {
        const title = window.prompt("Titel des neuen Projekts:", "Neues Projekt");
        if (!title) return;
        const res = await api<Project>("/api/admin/projects", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title, status: "draft", visibility: "public" }),
        });
        if (res.ok && res.data) nav(`/admin/projekte/${res.data.id}`);
        else setError(res.error || "Anlegen fehlgeschlagen.");
    };

    return (
        <div data-testid="admin-projekte">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Objekte</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Projekte</h1>
                    <p className="mt-1 text-hf-muted">Alle Objekte, die HypeFly dokumentiert hat.</p>
                </div>
                <HFButton onClick={create} variant="primary" icon={<Plus size={13} />} data-testid="projekte-new">
                    Neues Projekt
                </HFButton>
            </div>
            {error && <div className="mt-6 p-3 text-sm rounded" style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}>{error}</div>}

            <div className="mt-8 rounded-sm overflow-hidden" style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }} data-testid="projekte-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left uppercase tracking-[0.14em] text-[10px] text-hf-muted" style={{ background: "hsl(var(--hf-surface))" }}>
                                <th className="px-4 py-3">Titel</th>
                                <th className="px-4 py-3">Slug</th>
                                <th className="px-4 py-3">Objekt</th>
                                <th className="px-4 py-3">Sichtbar</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Aktualisiert</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-hf-muted">Noch keine Projekte.</td></tr>
                            )}
                            {items.map((p) => (
                                <tr key={p.id} className="border-t hover:bg-hf-surface" style={{ borderColor: "hsl(var(--hf-line))" }} data-testid={`projekte-row-${p.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Link to={`/admin/projekte/${p.id}`} className="font-medium hover:text-hf-gold-dark">{p.title}</Link>
                                            {p.is_demo && (
                                                <span
                                                    className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                    style={{ background: "hsl(45 90% 60%)", color: "hsl(var(--hf-ink))" }}
                                                    data-testid={`projekte-demo-badge-${p.id}`}
                                                >
                                                    Demo
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-hf-muted">{p.subtitle}</div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-hf-muted">/projekt/{p.slug}</td>
                                    <td className="px-4 py-3">{p.object_type || "—"}</td>
                                    <td className="px-4 py-3">{p.visibility === "private" ? <span className="inline-flex items-center gap-1"><EyeOff size={12} /> privat</span> : <span className="inline-flex items-center gap-1"><Eye size={12} /> öffentlich</span>}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-[10px] tracking-[0.14em] uppercase px-2 py-1 rounded-sm"
                                            style={{ background: p.status === "published" ? "hsl(140 40% 82%)" : "hsl(var(--hf-surface-2))", color: "hsl(var(--hf-ink))" }}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-hf-muted">{fmtDateTime(p.updated_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export function AdminProjektDetailPage() {
    const { id } = useParams<{ id: string }>();
    const nav = useNavigate();
    const [p, setP] = useState<Project | null>(null);
    const [error, setError] = useState("");
    const [savedTick, setSavedTick] = useState(false);
    const [availableMedia, setAvailableMedia] = useState<Media[]>([]);
    const [coverPickerOpen, setCoverPickerOpen] = useState(false);
    const [coverSearch, setCoverSearch] = useState("");

    useEffect(() => {
        if (!id) return;
        void (async () => {
            const res = await api<Project>(`/api/admin/projects/${id}`);
            if (res.ok && res.data) setP(res.data);
            else setError(res.error || "Nicht gefunden.");
        })();
        void (async () => {
            const r = await api<{ items: Media[] }>("/api/admin/media");
            if (r.ok && r.data) setAvailableMedia(r.data.items);
        })();
    }, [id]);

    const patch = async (updates: Partial<Project> & { password?: string }) => {
        if (!p) return;
        const res = await api<Project>(`/api/admin/projects/${p.id}`, {
            method: "PATCH", headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updates),
        });
        if (res.ok && res.data) {
            setP(res.data);
            setSavedTick(true);
            window.setTimeout(() => setSavedTick(false), 1500);
        } else {
            setError(res.error || "Speichern fehlgeschlagen.");
        }
    };

    const togglePublish = async () => {
        if (!p) return;
        const action = p.status === "published" ? "unpublish" : "publish";
        if (action === "publish") {
            // Bestätigungen bei häufigen Publish-Fallstricken
            if (p.title.trim() === "Neues Projekt") {
                if (!window.confirm(
                    'Ihr Projekt-Titel lautet noch „Neues Projekt". Wirklich so veröffentlichen?',
                )) return;
            }
            if (!p.cover_media_id && p.media_ids.length > 0) {
                // Nur informieren, nicht blockieren
                const proceed = window.confirm(
                    "Kein Cover-Bild gewählt. Wir verwenden das Poster des ersten Mediums als Hintergrund.\n\n" +
                    "Empfehlung: dediziertes Landscape-Foto als Cover setzen (Rundgang-Video-Poster wirken oft verzerrt).\n\n" +
                    "Trotzdem jetzt veröffentlichen?",
                );
                if (!proceed) return;
            }
        }
        const res = await api(`/api/admin/projects/${p.id}/${action}`, { method: "POST" });
        if (res.ok) {
            const r = await api<Project>(`/api/admin/projects/${p.id}`);
            if (r.ok && r.data) setP(r.data);
        }
    };

    const remove = async () => {
        if (!p) return;
        if (!window.confirm(`Projekt "${p.title}" wirklich löschen?`)) return;
        const res = await api(`/api/admin/projects/${p.id}`, { method: "DELETE" });
        if (res.ok) nav("/admin/projekte");
    };

    const toggleMedia = (mid: string) => {
        if (!p) return;
        const next = p.media_ids.includes(mid)
            ? p.media_ids.filter((x) => x !== mid)
            : [...p.media_ids, mid];
        void patch({ media_ids: next });
    };

    if (error) return <div className="p-6 text-hf-danger">{error}</div>;
    if (!p) return <div className="text-hf-muted">Wird geladen…</div>;

    const input = "w-full px-3 py-2 border rounded-sm bg-white text-sm";
    const inputStyle = { borderColor: "hsl(var(--hf-line))" };

    return (
        <div data-testid="admin-projekt-detail" className="max-w-5xl">
            <Link to="/admin/projekte" className="inline-flex items-center gap-1 text-sm text-hf-muted hover:text-hf-ink">
                <ArrowLeft size={14} /> Zurück
            </Link>
            <div className="mt-3 flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Projekt</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">{p.title}</h1>
                    <div className="mt-1 text-hf-muted text-sm font-mono">/projekt/{p.slug}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <a href={`/projekt/${p.slug}?preview=1`} target="_blank" rel="noreferrer" className="hf-btn hf-btn-ghost" data-testid="projekt-preview">
                        <ExternalLink size={13} /><span>Vorschau</span>
                    </a>
                    <HFButton variant={p.status === "published" ? "ghost" : "primary"} onClick={togglePublish} data-testid="projekt-publish-toggle">
                        {p.status === "published" ? "Zurückziehen" : "Veröffentlichen"}
                    </HFButton>
                    <HFButton variant="ghost" onClick={remove} icon={<Trash2 size={13} />} data-testid="projekt-delete">
                        Löschen
                    </HFButton>
                    <span className="text-xs text-hf-muted h-5">{savedTick && "Gespeichert."}</span>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Titel</div>
                        <input className={input} style={inputStyle} value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} onBlur={(e) => void patch({ title: e.target.value })} />
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Slug</div>
                        <input className={input} style={inputStyle} value={p.slug} onChange={(e) => setP({ ...p, slug: e.target.value })} onBlur={(e) => void patch({ slug: e.target.value })} />
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Untertitel</div>
                        <input className={input} style={inputStyle} value={p.subtitle} onChange={(e) => setP({ ...p, subtitle: e.target.value })} onBlur={(e) => void patch({ subtitle: e.target.value })} />
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Kurzbeschreibung</div>
                        <textarea className={input} style={inputStyle} rows={3} value={p.description_short} onChange={(e) => setP({ ...p, description_short: e.target.value })} onBlur={(e) => void patch({ description_short: e.target.value })} />
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Ausführliche Beschreibung</div>
                        <textarea className={input} style={inputStyle} rows={6} value={p.description_long} onChange={(e) => setP({ ...p, description_long: e.target.value })} onBlur={(e) => void patch({ description_long: e.target.value })} />
                    </div>
                </div>
                <div className="space-y-4">
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Objektart</div>
                        <select className={input} style={inputStyle} value={p.object_type} onChange={(e) => { setP({ ...p, object_type: e.target.value }); void patch({ object_type: e.target.value }); }}>
                            <option value="">—</option>
                            {OBJECT_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Ort</div>
                        <input className={input} style={inputStyle} value={p.location} onChange={(e) => setP({ ...p, location: e.target.value })} onBlur={(e) => void patch({ location: e.target.value })} />
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Aufnahmedatum</div>
                        <input type="date" className={input} style={inputStyle} value={p.capture_date ? p.capture_date.slice(0, 10) : ""} onChange={(e) => void patch({ capture_date: e.target.value })} />
                    </div>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Sichtbarkeit</div>
                        <select className={input} style={inputStyle} value={p.visibility} onChange={(e) => void patch({ visibility: e.target.value as "public" | "private" })}>
                            <option value="public">Öffentlich</option>
                            <option value="private">Privat</option>
                        </select>
                    </div>
                    {p.visibility === "private" && (
                        <div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={p.password_protected} onChange={(e) => void patch({ password_protected: e.target.checked })} />
                                <span>Mit Passwort geschützt</span>
                            </label>
                            {p.password_protected && (
                                <input type="password" placeholder="Neues Passwort setzen"
                                    className={input + " mt-2"} style={inputStyle}
                                    onBlur={(e) => { if (e.target.value) { void patch({ password: e.target.value }); e.target.value = ""; } }} />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* SICHTBARKEIT AUF ÜBERSICHTSSEITEN */}
            <div className="mt-10" data-testid="projekt-showcase-section">
                <div className="hf-eyebrow">Sichtbarkeit auf Übersichtsseiten</div>
                <h3 className="font-display text-2xl mt-2">Wo darf dieses Projekt erscheinen?</h3>
                <p className="text-xs text-hf-muted mt-1">
                    Nur öffentliche, veröffentlichte Projekte erscheinen — die Auswahl hier steuert, auf welchen Übersichtsseiten sie zusätzlich sichtbar sind.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-4">
                    <label
                        className="flex items-start gap-3 p-4 rounded-sm cursor-pointer flex-1"
                        style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="projekt-show-on-main-label"
                    >
                        <input
                            type="checkbox"
                            checked={!!p.show_on_main}
                            onChange={(e) => void patch({ show_on_main: e.target.checked })}
                            className="mt-0.5"
                            data-testid="projekt-show-on-main"
                        />
                        <span>
                            <span className="block text-sm font-medium">Auf Hauptseite zeigen</span>
                            <span className="block text-xs text-hf-muted mt-0.5">Erscheint im Showcase auf der Startseite.</span>
                        </span>
                    </label>
                    <label
                        className="flex items-start gap-3 p-4 rounded-sm cursor-pointer flex-1"
                        style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="projekt-show-on-sylt-label"
                    >
                        <input
                            type="checkbox"
                            checked={!!p.show_on_sylt}
                            onChange={(e) => void patch({ show_on_sylt: e.target.checked })}
                            className="mt-0.5"
                            data-testid="projekt-show-on-sylt"
                        />
                        <span>
                            <span className="block text-sm font-medium">Auf Sylt-Seite zeigen</span>
                            <span className="block text-xs text-hf-muted mt-0.5">Erscheint im Referenz-Grid auf /sylt.</span>
                        </span>
                    </label>
                </div>
            </div>

            {/* COVER-BILD Sektion */}
            <div className="mt-10" data-testid="projekt-cover-section">
                <div className="hf-eyebrow">Cover-Bild</div>
                <h3 className="font-display text-2xl mt-2">Für Hero und Referenzkarten</h3>
                <p className="text-xs text-hf-muted mt-1">Empfohlen: Landscape-Foto (nicht equirekt-360°). Wenn leer, wird das Poster des ersten Mediums verwendet.</p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                    <div
                        className="relative rounded-sm overflow-hidden flex-shrink-0"
                        style={{
                            width: 240, height: 160,
                            background: "hsl(var(--hf-surface))",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                        data-testid="projekt-cover-preview"
                    >
                        {(() => {
                            const cm = p.cover_media_id ? availableMedia.find(x => x.id === p.cover_media_id) : null;
                            const src = cm ? mediaThumb(cm, BACKEND_URL) : null;
                            if (src) return <img src={src} alt="Cover" className="w-full h-full object-cover" />;
                            return (
                                <div className="w-full h-full flex flex-col items-center justify-center text-hf-muted p-3 text-center">
                                    <ImageIcon size={22} />
                                    <span className="mt-1 text-[10px] leading-tight">Kein Cover — es wird das Poster des ersten Mediums verwendet.</span>
                                </div>
                            );
                        })()}
                    </div>
                    <div className="flex flex-col gap-2">
                        <button type="button" onClick={() => setCoverPickerOpen(true)}
                            className="hf-btn hf-btn-primary" data-testid="projekt-cover-pick">
                            Aus Medien wählen
                        </button>
                        {p.cover_media_id && (
                            <button type="button" onClick={() => void patch({ cover_media_id: null })}
                                className="hf-btn hf-btn-ghost" data-testid="projekt-cover-clear">
                                Entfernen
                            </button>
                        )}
                        {p.cover_media_id && (
                            <div className="mt-1">
                                <AiFlagToggle
                                    mediaId={p.cover_media_id}
                                    compact
                                    testIdPrefix="projekt-cover-ai-flag"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* VORHER · NACHHER — dedizierte Projekt-Pairs */}
            <PairsEditor
                project={p}
                availableMedia={availableMedia}
                onReload={async () => {
                    const res = await api<Project>(`/api/admin/projects/${p.id}`);
                    if (res.ok && res.data) setP(res.data);
                }}
            />

            <div className="mt-10">
                <div className="hf-eyebrow">Medien-Zuordnung</div>
                <h3 className="font-display text-2xl mt-2">{p.media_ids.length} zugeordnet</h3>
                <p className="text-xs text-hf-muted mt-1">Klick auf eine Kachel toggelt die Zuordnung. Klick auf ★ setzt das Bild als Cover.</p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="projekt-media-grid">
                    {availableMedia.length === 0 && (
                        <div className="col-span-full text-sm text-hf-muted">Bitte lade zuerst Medien in der <Link to="/admin/medien" className="underline">Bibliothek</Link> hoch.</div>
                    )}
                    {availableMedia.map((m) => {
                        const active = p.media_ids.includes(m.id);
                        const isCover = p.cover_media_id === m.id;
                        const thumb = mediaThumb(m, BACKEND_URL);
                        return (
                            <div key={m.id}
                                className="text-left group relative aspect-[4/3] rounded-sm overflow-hidden"
                                style={{ border: isCover ? "2px solid hsl(var(--hf-gold-dark))" : active ? "2px solid hsl(var(--hf-gold))" : "1px solid hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                                data-testid={`projekt-media-${m.id}`}>
                                <button type="button" onClick={() => toggleMedia(m.id)}
                                    className="absolute inset-0 w-full h-full text-left"
                                    aria-label={active ? "Aus Projekt entfernen" : "Zum Projekt hinzufügen"}>
                                    {thumb ? (
                                        <img src={thumb} alt={m.title} loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center" style={{ background: "hsl(var(--hf-surface))" }}>
                                            <Video size={22} style={{ color: "hsl(var(--hf-muted))" }} />
                                        </div>
                                    )}
                                </button>
                                {/* Star für Cover-Auswahl */}
                                <button type="button"
                                    onClick={(e) => { e.stopPropagation(); void patch({ cover_media_id: isCover ? null : m.id }); }}
                                    aria-label={isCover ? "Aktuelles Cover-Bild" : "Als Cover-Bild setzen"}
                                    title={isCover ? "Aktuelles Cover-Bild" : "Als Cover-Bild setzen"}
                                    className="absolute top-1 left-1 w-7 h-7 rounded-full inline-flex items-center justify-center transition-colors z-10"
                                    style={{
                                        background: isCover ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-ink) / 0.55)",
                                        color: isCover ? "hsl(var(--hf-bg))" : "hsl(var(--hf-gold-soft))",
                                    }}
                                    data-testid={`projekt-media-star-${m.id}`}>
                                    <Star size={13} fill={isCover ? "currentColor" : "none"} />
                                </button>
                                {m.mime.startsWith("video/") && (
                                    <span className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}>
                                        <Video size={11} />
                                    </span>
                                )}
                                {active && (
                                    <span className="absolute top-1 right-1 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs pointer-events-none z-10" style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}>
                                        ✓
                                    </span>
                                )}
                                {/* Icon-Only KI-Toggle pro Kachel. Sitzt am unteren
                                    rechten Rand, links neben dem Video-Chip
                                    (falls Video), damit keine Kollision entsteht. */}
                                <span
                                    className="absolute bottom-1 z-10"
                                    style={{ right: m.mime.startsWith("video/") ? 34 : 4 }}
                                >
                                    <AiFlagToggle
                                        mediaId={m.id}
                                        variant="icon"
                                        testIdPrefix={`projekt-grid-ai-flag-${m.id}`}
                                    />
                                </span>
                                <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm pointer-events-none" style={{ background: "hsl(var(--hf-ink) / 0.78)", color: "hsl(var(--hf-gold-soft))" }}>
                                    {m.media_type}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Cover-Picker-Modal */}
            {coverPickerOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6"
                    style={{ background: "rgba(14,23,38,0.75)" }} onClick={() => setCoverPickerOpen(false)}
                    data-testid="cover-picker-modal">
                    <div className="w-full max-w-4xl rounded-sm overflow-hidden"
                        style={{ background: "hsl(var(--hf-bg))" }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "hsl(var(--hf-line))" }}>
                            <div>
                                <div className="hf-eyebrow">Cover</div>
                                <div className="font-display text-lg">Cover-Bild aus Bibliothek wählen</div>
                            </div>
                            <button onClick={() => setCoverPickerOpen(false)} aria-label="Schließen"
                                className="w-9 h-9 inline-flex items-center justify-center border rounded-sm"
                                style={{ borderColor: "hsl(var(--hf-line))" }}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="px-5 pt-4">
                            <label className="relative block">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-hf-muted" />
                                <input value={coverSearch} onChange={(e) => setCoverSearch(e.target.value)}
                                    placeholder="Nach Titel, Dateiname, Alt-Text suchen…"
                                    className="w-full pl-9 pr-3 py-2 border rounded-sm bg-white text-sm"
                                    style={{ borderColor: "hsl(var(--hf-line))" }}
                                    data-testid="cover-picker-search" />
                            </label>
                        </div>
                        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto">
                            {availableMedia
                                .filter((m) => {
                                    const q = coverSearch.trim().toLowerCase();
                                    if (!q) return true;
                                    return (m.title || "").toLowerCase().includes(q) ||
                                        (m.original_filename || "").toLowerCase().includes(q) ||
                                        (m.alt || "").toLowerCase().includes(q);
                                })
                                .map((m) => {
                                    const thumb = mediaThumb(m, BACKEND_URL);
                                    const active = p.cover_media_id === m.id;
                                    return (
                                        <button key={m.id} type="button"
                                            onClick={() => { void patch({ cover_media_id: m.id }); setCoverPickerOpen(false); }}
                                            className="relative aspect-[4/3] rounded-sm overflow-hidden text-left"
                                            style={{ border: active ? "2px solid hsl(var(--hf-gold-dark))" : "1px solid hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                                            data-testid={`cover-picker-item-${m.id}`}>
                                            {thumb ? (
                                                <img src={thumb} alt={m.title} loading="lazy" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Video size={22} style={{ color: "hsl(var(--hf-muted))" }} />
                                                </div>
                                            )}
                                            <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm"
                                                style={{ background: "hsl(var(--hf-ink) / 0.78)", color: "hsl(var(--hf-gold-soft))" }}>
                                                {m.media_type}
                                            </span>
                                            {active && (
                                                <span className="absolute top-1 right-1 w-6 h-6 rounded-full inline-flex items-center justify-center text-xs" style={{ background: "hsl(var(--hf-gold-dark))", color: "hsl(var(--hf-bg))" }}>
                                                    ✓
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
